"use client";

import { useEffect, useState } from "react";
import Image, { type ImageProps } from "next/image";
import clsx from "clsx";
import { resolveOptimizedImageUrl } from "@/modules/web/utils/image";

type Props = Omit<ImageProps, "src"> & {
  src?: string | null;
  fallbackSrc?: string;
};

const DEFAULT_AVATAR_SRC = "/f/images/default_avatar_2.png";

export default function AvatarImage({
  src,
  alt,
  className,
  fallbackSrc = DEFAULT_AVATAR_SRC,
  ...props
}: Props) {
  // resolveOptimizedImageUrl() assumes the optimizer (not the browser) fetches
  // this src — correct as long as no caller passes `unoptimized` below. If one
  // ever does, its src must come from resolveImageUrl() instead.
  const [currentSrc, setCurrentSrc] = useState(resolveOptimizedImageUrl(src) || fallbackSrc);

  // Sync state with props when src changes (essential for live previews)
  useEffect(() => {
    setCurrentSrc(resolveOptimizedImageUrl(src) || fallbackSrc);
  }, [src, fallbackSrc]);

  return (
    <Image
      {...props}
      src={currentSrc}
      alt={alt}
      className={clsx("object-cover", className)}
      onError={() => {
        if (currentSrc !== fallbackSrc) {
          setCurrentSrc(fallbackSrc);
        }
      }}
    />
  );
}
